import React from 'react';
import { useTranslation } from 'react-i18next';
import { createTikTokChannelAccount } from '../services/channelAccountService';
import { ToggleField } from '../../../shared/components';
import styles from './ConnectorsPage.module.css';

interface AddTikTokAccountFormProps {
  canManage: boolean;
  onCreated: () => void;
  onCancel: () => void;
}

const emptyForm = {
  displayName: '',
  businessOpenId: '',
  accessToken: '',
  isDefault: false,
};

export const AddTikTokAccountForm: React.FC<AddTikTokAccountFormProps> = ({
  canManage,
  onCreated,
  onCancel,
}) => {
  const { t } = useTranslation(['companies', 'admin']);
  const [form, setForm] = React.useState(emptyForm);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const save = async () => {
    if (!form.displayName.trim() || !form.businessOpenId.trim() || !form.accessToken.trim()) {
      setError(t('channelAccountAddRequiredFields', { ns: 'companies' }));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await createTikTokChannelAccount({
        displayName: form.displayName.trim(),
        businessOpenId: form.businessOpenId.trim(),
        accessToken: form.accessToken.trim(),
        isDefault: form.isDefault,
      });
      setForm(emptyForm);
      onCreated();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : t('failedToCreateChannelAccount', { ns: 'companies' }));
    } finally {
      setSaving(false);
    }
  };

  if (!canManage) {
    return null;
  }

  return (
    <div className={styles.channelAccountAddForm}>
      <h4>{t('channelAccountAddTikTokHeading', { ns: 'companies' })}</h4>
      <p style={{ color: '#516173', marginTop: 0 }}>
        {t('channelAccountAddTikTokHelp', { ns: 'companies' })}
      </p>

      {error ? <div className="error">{error}</div> : null}

      <div className="company-form-grid">
        <label>
          {t('channelAccountDisplayName', { ns: 'companies' })}
          <input
            className="text-input"
            value={form.displayName}
            onChange={event => setForm(prev => ({ ...prev, displayName: event.target.value }))}
            placeholder={t('channelAccountDisplayNamePlaceholder', { ns: 'companies' })}
          />
        </label>

        <label>
          {t('tiktokBusinessOpenId', { ns: 'companies' })}
          <input
            className="text-input"
            value={form.businessOpenId}
            onChange={event => setForm(prev => ({ ...prev, businessOpenId: event.target.value }))}
          />
        </label>

        <label>
          {t('tiktokAccessToken', { ns: 'companies' })}
          <input
            className="text-input"
            type="password"
            value={form.accessToken}
            onChange={event => setForm(prev => ({ ...prev, accessToken: event.target.value }))}
            autoComplete="new-password"
          />
        </label>

        <ToggleField
          label={t('channelAccountDefaultLabel', { ns: 'companies' })}
          checked={form.isDefault}
          onChange={value => setForm(prev => ({ ...prev, isDefault: value }))}
        />
      </div>

      <div className="company-actions-inline" style={{ marginTop: 12 }}>
        <button type="button" className="btn btn-primary" onClick={() => { void save(); }} disabled={saving}>
          {saving ? t('savingEllipsis', { ns: 'admin' }) : t('channelAccountCreate', { ns: 'companies' })}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>
          {t('channelAccountCancelAdd', { ns: 'companies' })}
        </button>
      </div>
    </div>
  );
};
